import routes from 'constants/routes';
import { getDetailOfDealOrCaseInCRM } from 'features/common/helpers';
import { ITransactionLineToDetail } from 'features/transactions/types';
import { formatTransactionAmount } from 'features/transaction-details/helpers';
import {
	formatFinalizationColloquialLine,
	sortFinalizationsChronologically,
} from 'features/transaction-details/helpers/finalizationCopy';
import { IAuthorizationWithCrosses, ITransactionFullDetails } from '../types';

const buildAuthorizationStatusLine = (
	transaction: ITransactionLineToDetail,
	authorizations: IAuthorizationWithCrosses[],
): string => {
	const { amount, pendingAuthorization, coinCode } = transaction;

	const activeParts = authorizations
		.filter((authorization) => authorization.amountAuthorized > 0)
		.map(
			(authorization) =>
				`${formatTransactionAmount(authorization.amountAuthorized, authorization.coinCode)} contra ${
					authorization.chargedAgainstOptionName
				}`,
		);

	if (activeParts.length === 0) {
		if (pendingAuthorization >= amount) {
			return 'Aún pendiente de autorizar por el total.';
		}
		return `Aún sin autorizar (${formatTransactionAmount(pendingAuthorization, coinCode)} pendientes).`;
	}

	const authorizedText = `Autorizada por ${activeParts.join(' y ')}`;

	if (pendingAuthorization > 0) {
		return `${authorizedText}, con ${formatTransactionAmount(pendingAuthorization, coinCode)} pendientes a autorizar.`;
	}

	return `${authorizedText}.`;
};

export const buildTransactionContextText = (details: ITransactionFullDetails, transactionId: number): string => {
	const origin = typeof window !== 'undefined' ? window.location.origin : '';
	const transaction = details.transaction.transactionData;
	const { coinCode, amount, crmTicket, isBitrixDeal } = transaction;
	const transactionUrl = `${origin}${routes.transactions.detailsUnified.replace(':id', String(transactionId))}`;
	const caseUrl = getDetailOfDealOrCaseInCRM(crmTicket, isBitrixDeal);
	const caseLabel = isBitrixDeal ? 'deal de venta' : 'caso de venta';
	const amountText = formatTransactionAmount(amount, coinCode);

	let text = `La transacción ${transactionId} (${transactionUrl}) por un monto de ${amountText} fue realizada para cobrar `;

	if (details.saleOrder) {
		const saleOrderUrl = `${origin}${routes.salesOrder.edit.replace(':id', details.saleOrder.id)}`;
		text += `la orden de venta ${details.saleOrder.id} N° ${details.saleOrder.orderNumber} (${saleOrderUrl}) asociada al ${caseLabel} ${crmTicket} (${caseUrl}).`;
	} else {
		text += `el ${caseLabel} ${crmTicket} (${caseUrl}).`;
	}

	if (details.saleOrder?.createdBy) {
		text += ` Vendedor: ${details.saleOrder.createdBy}.`;
	}

	const finalizations = sortFinalizationsChronologically(details.finalizationsData ?? []);
	if (finalizations.length > 0) {
		const finalizationText = finalizations
			.map((finalization, index) => formatFinalizationColloquialLine(finalization, index, coinCode))
			.join(' ');
		text += ` ${finalizationText}`;
	}

	text += ` Estado: ${buildAuthorizationStatusLine(transaction, details.authorizations)}`;

	return text;
};
